import React from "react";
import { Link } from "react-router-dom";
import Head from "../../layout/head/Head";
import LogoDark2x from "../../images/logo-dark2x.png";
import BrandPanel from "./BrandPanel";
import "./auth.css";

// Split-screen shell shared by every auth screen: form on the left, brand
// panel on the right. BrandPanel is a sibling of the form pane, not a child,
// so it can be display:none'd below 1100px without disturbing the form.
//
// Pages own their own <form> and handlers — this only supplies chrome. The
// form primitives (.pxauth-field / .pxauth-label / .pxauth-input /
// .pxauth-btn / .pxauth-error) live in auth.css.

type AuthShellProps = {
  title: string;
  heading: string;
  subheading?: React.ReactNode;
  children: React.ReactNode;
  footNote?: React.ReactNode;
  brandHeadline?: string;
  brandSubcopy?: string;
  size?: "default" | "wide";
};

// Replaces AuthFooter for these screens: a fixed-height footer under a 100vh
// split screen would overflow. Same links, minus AuthFooter's dead language
// switcher.
const LegalLine: React.FC = () => (
  <p className="pxauth__legal">
    &copy; {new Date().getFullYear()} Procurex Technologies PVT LTD.
    <span className="pxauth__legalSep">&middot;</span>
    <Link target="_blank" to={`${process.env.PUBLIC_URL}/auths/terms`}>
      Terms
    </Link>
    <span className="pxauth__legalSep">&middot;</span>
    <Link target="_blank" to={`${process.env.PUBLIC_URL}/auths/terms`}>
      Privacy
    </Link>
    <span className="pxauth__legalSep">&middot;</span>
    <Link target="_blank" to={`${process.env.PUBLIC_URL}/auths/faq`}>
      Help
    </Link>
  </p>
);

const AuthShell: React.FC<AuthShellProps> = ({
  title,
  heading,
  subheading,
  children,
  footNote,
  brandHeadline,
  brandSubcopy,
  size = "default",
}) => (
  <>
    <Head title={title} />
    <div className="pxauth">
      <main className="pxauth__shell">
        <section className="pxauth__pane">
          <div
            className={`pxauth__body${
              size === "wide" ? " pxauth__body--wide" : ""
            }`}
          >
            <header className="pxauth__brandmark">
              <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                <img className="pxauth__logo" src={LogoDark2x} alt="Procurex" />
              </Link>
              <span className="pxauth__sep" aria-hidden="true" />
              <span className="pxauth__tag">Project Management System</span>
            </header>
            <h1 className="pxauth__title">{heading}</h1>
            {subheading && <p className="pxauth__subtitle">{subheading}</p>}
            {children}
            {footNote && <p className="pxauth__note">{footNote}</p>}
          </div>

          <LegalLine />
        </section>

        <BrandPanel headline={brandHeadline} subcopy={brandSubcopy} />
      </main>
    </div>
  </>
);

export default AuthShell;
